import type { IndicatorPeriod } from '@Application/Indicators/IndicatorScope';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import homePeriodSignal, { setHomePeriod } from '@UI/Pages/Home/Signals/homePeriodSignal';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';

type Props = {
	/** Shows the visible "Période" label (narrow viewport); the accessible name is always there. */
	showLabel: boolean;
};

const PERIOD_OPTIONS: ReadonlyArray<{ readonly value: IndicatorPeriod; readonly label: string }> =
	Object.freeze([
		{ value: 7, label: '7 j' },
		{ value: 30, label: '30 j' },
		{ value: 90, label: '90 j' },
	]);

const wrapperStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const labelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1;
	color: ${theme.colors.inkMuted};
`;

const groupStyle = css`
	display: inline-flex;
	align-self: flex-start;
	gap: 2px;
	padding: 3px;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.borderStrong};
`;

const optionStyle = css`
	padding: 0.5625rem 0.9375rem;
	border: 0;
	border-radius: ${theme.radii.pill};
	background-color: transparent;
	color: ${theme.colors.inkSecondary};
	font-family: ${theme.fonts.mono};
	font-size: 0.78125rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1;
	cursor: pointer;

	&:hover:not([aria-checked='true']) {
		background-color: ${theme.colors.surfaceMuted};
		color: ${theme.colors.ink};
	}

	&[aria-checked='true'] {
		background-color: ${theme.colors.accent};
		color: ${theme.colors.surface};
		font-weight: ${theme.fontWeights.semibold};
	}

	&:focus-visible {
		outline: 3px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

/** 7 / 30 / 90 days, always one selected: a radio group with arrow-key navigation. */
const PeriodFilter: React.FC<Props> = ({ showLabel }) => {
	const period = useSignalValue(homePeriodSignal);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			const step =
				event.key === 'ArrowRight' || event.key === 'ArrowDown'
					? 1
					: event.key === 'ArrowLeft' || event.key === 'ArrowUp'
						? -1
						: 0;

			if (step === 0) {
				return;
			}

			event.preventDefault();
			const currentIndex = PERIOD_OPTIONS.findIndex((option) => option.value === period);
			const next =
				PERIOD_OPTIONS[(currentIndex + step + PERIOD_OPTIONS.length) % PERIOD_OPTIONS.length];

			if (next) {
				setHomePeriod(next.value);
				const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]');
				buttons[PERIOD_OPTIONS.indexOf(next)]?.focus();
			}
		},
		[period],
	);

	const handleClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		const value = Number(event.currentTarget.dataset.value);

		if (value === 7 || value === 30 || value === 90) {
			setHomePeriod(value);
		}
	}, []);

	return (
		<div css={wrapperStyle}>
			{showLabel && (
				<span css={labelStyle} aria-hidden="true">
					Période
				</span>
			)}
			<div role="radiogroup" aria-label="Période" css={groupStyle} onKeyDown={handleKeyDown}>
				{PERIOD_OPTIONS.map((option) => (
					// biome-ignore lint/a11y/useSemanticElements: button styled as a radio of the segmented control
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={option.value === period}
						tabIndex={option.value === period ? 0 : -1}
						data-value={option.value}
						css={optionStyle}
						onClick={handleClick}
					>
						{option.label}
					</button>
				))}
			</div>
		</div>
	);
};

export default PeriodFilter;
